import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

const REASONS: Record<string, string> = {
  'automation-off': 'Unit details is switched off in WhatsApp → Automations.',
  'not-allowed-by-master-switch': 'Auto-replies are Off, or in Test mode and this number is not a test number.',
  'opted-out': 'This customer opted out of WhatsApp messages.',
  'template-not-approved': 'The unit details template is not approved by Meta yet.',
  'no-phone': 'This lead has no valid 10-digit mobile number.',
  'no-settings': 'WhatsApp settings are not saved yet.',
  'lead-missing': 'Lead not found.',
};

/**
 * Executive sends the unit discussed to the lead on WhatsApp. Prices are
 * never sent: the message invites them to see pricing at a site visit.
 */
const SendPriceDialog = ({
  open,
  onClose,
  leadId,
  leadName,
  projectName,
}: {
  open: boolean;
  onClose: () => void;
  leadId: string;
  leadName: string;
  projectName: string;
}) => {
  const [unit, setUnit] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const close = () => {
    if (sending) return;
    setResult(null);
    onClose();
  };

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const res: any = await httpsCallable(functions, 'whatsappSendPriceDetails')({ leadId, unit: unit.trim() });
      const outcome = String(res?.data?.result || '');
      if (outcome === 'sent') {
        setResult({ type: 'success', text: 'Sent on WhatsApp and logged on the lead timeline.' });
        setUnit('');
      } else {
        setResult({ type: 'error', text: REASONS[outcome] || `Not sent: ${outcome}` });
      }
    } catch (err: any) {
      setResult({ type: 'error', text: String(err?.message || err) });
    } finally {
      setSending(false);
    }
  };

  const first = String(leadName || '').trim().split(/\s+/)[0] || 'there';

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>Send unit details on WhatsApp</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <TextField size="small" label="Unit" placeholder="2 BHK, 7th floor, A-704" value={unit} onChange={(e) => setUnit(e.target.value)} autoFocus />
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'pre-line', bgcolor: '#f5f3ef', borderRadius: 1, p: 1.25 }}>
            {`Hi ${first}, as discussed, here are the details for ${projectName || 'the project'}:\nUnit: ${unit || '…'}\n\nFor the latest pricing and availability, we’d be happy to show you the available options during a site visit.`}
          </Typography>
          {result && <Alert severity={result.type} sx={{ fontSize: 12.5, py: 0 }}>{result.text}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} sx={{ textTransform: 'none' }}>Close</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={sending || !unit.trim()}
          onClick={send}
          sx={{ textTransform: 'none', bgcolor: '#9d6f32', '&:hover': { bgcolor: '#86602b' } }}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendPriceDialog;
